import type {
  Attribute,
  CreateSkuRuleRequest,
  SkuRule,
  SkuRulePreviewRequest,
  UpdateSkuRuleRequest,
} from '@nucleus-commerce/api-client/admin';

import type { SkuRuleFormInput, SkuRuleFormValues } from '../schemas/sku-rule-form-schema';

/**
 * The presets the editor offers: plain templates, nothing special in the engine. A null root
 * means style numbers stay typed by hand and the rule writes variant SKUs only.
 */
export const SKU_RULE_PRESETS = [
  { key: 'jewellery', rootTemplate: '{type}{seq}', variantTemplate: '{root}-{options}' },
  { key: 'styleAndMetal', rootTemplate: '{type}{seq}', variantTemplate: '{root}-{option.metal}' },
  { key: 'prefixAndNumber', rootTemplate: 'NUC-{seq|pad:6}', variantTemplate: '{root}-{options}' },
  { key: 'variantsOnly', rootTemplate: null, variantTemplate: '{root}-{options}' },
] as const;

export type SkuRulePresetKey = (typeof SKU_RULE_PRESETS)[number]['key'];

/** Tokens the chip row offers per template; `{option.<key>}` chips come from the dictionary. */
export const ROOT_TOKENS = ['{type}', '{seq}', '{vendor}', '{title}', '{yy}', '{yyyy}'] as const;
export const VARIANT_TOKENS = ['{root}', '{options}', '{type}', '{vendor}', '{yy}'] as const;

/** Modifiers a token takes; the ones with `:N` insert a sample count the user edits. */
export const TOKEN_MODIFIERS = [
  '|first:3',
  '|last:2',
  '|upper',
  '|lower',
  '|digits',
  '|nodot',
  '|pad:6',
] as const;

/** One `{option.<key>}` chip per attribute products may build variants from. */
export const optionTokens = (attributes: readonly Attribute[]): string[] =>
  attributes.filter((a) => a.usableAsOption).map((a) => `{option.${a.key}}`);

/** `text` with `token` inserted at the caret; the caret is placed after it. */
export function insertAtCursor(
  text: string,
  token: string,
  selection: { start: number; end: number } | null,
): { text: string; caret: number } {
  const start = selection?.start ?? text.length;
  const end = selection?.end ?? text.length;
  return { text: `${text.slice(0, start)}${token}${text.slice(end)}`, caret: start + token.length };
}

/**
 * `text` with `modifier` (`|first:3`) added inside a token, since the grammar only reads
 * modifiers between the braces (`{title|first:3}`). The target is the token holding the caret,
 * else the nearest one before it, else the first one after it. A modifier of the same name is
 * replaced, not repeated. The caret lands before the closing brace so the next modifier joins
 * the same token. Null when the text has no token to modify.
 */
export function appendModifier(
  text: string,
  modifier: string,
  selection: { start: number; end: number } | null,
): { text: string; caret: number } | null {
  const tokens = [...text.matchAll(/\{[^{}]*\}/g)].map((match) => ({
    start: match.index,
    end: match.index + match[0].length,
  }));
  const at = selection?.start ?? text.length;
  const target =
    tokens.find((token) => at > token.start && at <= token.end) ??
    tokens.filter((token) => token.end <= at).at(-1) ??
    tokens.find((token) => token.start >= at);
  if (!target) return null;

  const added = modifier.replace(/^\|/, '');
  const name = modifierName(added);
  const [head = '', ...modifiers] = text.slice(target.start + 1, target.end - 1).split('|');
  const kept = modifiers.filter((part) => modifierName(part) !== name);
  const token = `{${[head, ...kept, added].join('|')}}`;
  return {
    text: `${text.slice(0, target.start)}${token}${text.slice(target.end)}`,
    caret: target.start + token.length - 1,
  };
}

const modifierName = (part: string) => part.split(':')[0]?.trim() ?? '';

/**
 * The backend's suggestion, kept simple: metal karat + colour initial ("14K Yellow Gold" →
 * 14KY), grade-like short words joined ("EF / VS1" → EFVS1), carats in hundredths ("1.50 ct" →
 * 150), otherwise initials of several words or the first three letters of one word.
 */
export function skuCodeFrom(label: string): string {
  const metal = /(\d{1,2})\s*k(?:t)?\b.*?\b(yellow|white|rose)/i.exec(label);
  if (metal) return `${metal[1]}K${(metal[2] ?? '').charAt(0).toUpperCase()}`;
  if (/^platinum/i.test(label)) return 'PT';
  const carat = /^(\d+(?:\.\d+)?)\s*ct$/i.exec(label);
  if (carat) return String(Math.round(Number(carat[1]) * 100)).padStart(3, '0');
  const words = label
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .split(/[^A-Za-z0-9]+/)
    .filter(Boolean);
  if (words.length === 0) return '';
  if (words.length > 1 && words.every((w) => w.length <= 4) && words.some((w) => /\d/.test(w))) {
    return words.join('').toUpperCase().slice(0, 12);
  }
  if (words.length > 1) {
    return words
      .map((w) => w.charAt(0))
      .join('')
      .toUpperCase()
      .slice(0, 12);
  }
  return (words[0] ?? '').slice(0, 3).toUpperCase();
}

/** Upper-cases as typed and drops anything a code cannot hold, so the field never shows a lowercase letter. */
export const normalizeSkuCode = (raw: string) =>
  raw
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, '')
    .slice(0, 12);

export function emptySkuRuleForm(): SkuRuleFormInput {
  return {
    name: '',
    rootManual: false,
    rootTemplate: '{type}{seq}',
    variantTemplate: '{root}-{options}',
    caseTransform: 'upper',
    numberPadding: '4',
    numberScope: 'rule',
    missingCodePolicy: 'derive',
    maxLength: '32',
    isDefault: false,
    productTypeIds: [],
    counters: [],
  };
}

export function fromSkuRule(rule: SkuRule): SkuRuleFormInput {
  return {
    name: rule.name,
    rootManual: rule.rootTemplate === null,
    rootTemplate: rule.rootTemplate ?? '',
    variantTemplate: rule.variantTemplate,
    caseTransform: rule.caseTransform,
    numberPadding: String(rule.numberPadding),
    numberScope: rule.numberScope,
    missingCodePolicy: rule.missingCodePolicy,
    maxLength: String(rule.maxLength),
    isDefault: rule.isDefault,
    productTypeIds: rule.productTypes.map((t) => t.id),
    counters: rule.counters.map((c) => ({
      scopeKey: c.scopeKey,
      nextNumber: String(c.nextNumber),
    })),
  };
}

const rootTemplateOf = (values: Pick<SkuRuleFormValues, 'rootManual' | 'rootTemplate'>) =>
  values.rootManual ? null : values.rootTemplate;

export function toSkuRuleCreate(values: SkuRuleFormValues): CreateSkuRuleRequest {
  return {
    name: values.name,
    rootTemplate: rootTemplateOf(values),
    variantTemplate: values.variantTemplate,
    caseTransform: values.caseTransform,
    numberPadding: Number(values.numberPadding),
    numberScope: values.numberScope,
    missingCodePolicy: values.missingCodePolicy,
    maxLength: Number(values.maxLength),
    isDefault: values.isDefault,
    productTypeIds: values.productTypeIds,
  };
}

/** Everything but the counters is sent whole; counters go only when moved, forward or not (the backend answers 422 for backwards). */
export function toSkuRuleUpdate(
  values: SkuRuleFormValues,
  existing: SkuRule,
): UpdateSkuRuleRequest {
  const moved = values.counters.filter((c) => {
    const current = existing.counters.find((e) => e.scopeKey === c.scopeKey);
    return current !== undefined && Number(c.nextNumber) !== current.nextNumber;
  });
  return {
    ...toSkuRuleCreate(values),
    ...(moved.length > 0
      ? { counters: moved.map((c) => ({ scopeKey: c.scopeKey, nextNumber: Number(c.nextNumber) })) }
      : {}),
  };
}

/** The preview takes unsaved values; the form's strings are parsed loosely so a half-typed number still previews. */
export function toSkuRulePreview(
  values: SkuRuleFormInput,
  options: { productTypeId: string | null; ruleId: string | null },
): SkuRulePreviewRequest {
  const padding = Number(values.numberPadding);
  const maxLength = Number(values.maxLength);
  return {
    rootTemplate: values.rootManual ? null : values.rootTemplate,
    variantTemplate: values.variantTemplate,
    caseTransform: values.caseTransform,
    ...(Number.isInteger(padding) && padding >= 0 && padding <= 8
      ? { numberPadding: padding }
      : {}),
    missingCodePolicy: values.missingCodePolicy,
    ...(Number.isInteger(maxLength) && maxLength >= 8 && maxLength <= 64 ? { maxLength } : {}),
    isDefault: values.isDefault,
    productTypeIds: values.productTypeIds,
    ...(options.productTypeId ? { productTypeId: options.productTypeId } : {}),
    ...(options.ruleId ? { ruleId: options.ruleId } : {}),
  };
}

/** Rules in list order: position, then name. */
export const byRulePosition = (a: SkuRule, b: SkuRule) =>
  a.position - b.position || a.name.localeCompare(b.name);
